import { Children, cloneElement, isValidElement, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode, type ReactElement } from 'react';
import { useOnClickOutside, useUncontrolled } from '@repletoryreach/sdk-utils/hooks';
import { Portal } from '../../core/Portal/Portal.js';
import { computePosition, type Placement } from '../floating-positioner.js';
import { useReducedMotionMultiplier } from '../../motion/hooks/useReducedMotionMultiplier.js';
import './Popover.css';

export interface PopoverProps {
  opened?: boolean;
  defaultOpened?: boolean;
  onChange?: (opened: boolean) => void;
  /** Default placement. */
  position?: Placement;
  /** Distance from anchor. */
  offset?: number;
  /** Close on outside click. Default: true. */
  closeOnClickOutside?: boolean;
  /** Close on Escape. Default: true. */
  closeOnEscape?: boolean;
  /** Visible arrow. */
  withArrow?: boolean;
  /** Show shadow. */
  shadow?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Custom width. */
  width?: number | string;
  /** Trapping focus while opened (uncommon for popovers). */
  trapFocus?: boolean;
  children?: ReactNode;
  className?: string;
}

interface TargetProps { children: ReactNode }
function Target({ children }: TargetProps) { return <>{children}</>; }
interface DropdownProps { children?: ReactNode }
function Dropdown({ children }: DropdownProps) { return <>{children}</>; }

/**
 * Composed popover. Usage:
 *
 *   <Popover position="bottom-start">
 *     <Popover.Target><Button>Open</Button></Popover.Target>
 *     <Popover.Dropdown>…</Popover.Dropdown>
 *   </Popover>
 *
 * Uses the lightweight `computePosition` from `floating-positioner.ts`
 * no peer dependency. If you need the full `@floating-ui/react` middleware
 * pipeline, render its `<FloatingPortal>` directly and skip this wrapper.
 */
function PopoverRoot({
  opened, defaultOpened, onChange,
  position = 'bottom-start', offset = 8,
  closeOnClickOutside = true, closeOnEscape = true,
  withArrow, shadow = 'md', width, children, className
}: PopoverProps): ReactNode {
  const [isOpen, setOpen] = useUncontrolled<boolean>({
    value: opened, defaultValue: defaultOpened, finalValue: false, onChange
  });
  const anchorRef = useRef<HTMLElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  /*
   * The dropdown node is held in STATE, not only in a ref.
   *
   * `Portal` renders null until its own effect has run, so on the commit where
   * the popover opens the dropdown does not exist yet. The measuring effect
   * below used to read a ref that was still null at that moment, bail out, and
   * never run again, because none of its dependencies changed when the portal
   * finally mounted. Every popover in both consoles therefore stayed at its
   * pre-measurement position of -9999px: open, focusable, and off-screen.
   *
   * A state-backed callback ref re-runs the effect the moment the node exists.
   */
  const [dropdownEl, setDropdownEl] = useState<HTMLDivElement | null>(null);
  const setDropdownNode = (node: HTMLDivElement | null) => {
    dropdownRef.current = node;
    setDropdownEl(node);
  };
  const [coords, setCoords] = useState<{ x: number; y: number; arrow: { x: number; y: number; side: string } } | null>(null);
  const multiplier = useReducedMotionMultiplier();
  const dropId = useId();

  // Position whenever opened or window resizes/scrolls.
  useLayoutEffect(() => {
    if (!isOpen || !anchorRef.current || !dropdownEl) return;
    const measure = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const aRect = anchor.getBoundingClientRect();
      const dRect = dropdownEl.getBoundingClientRect();
      setCoords(computePosition(aRect, { width: dRect.width, height: dRect.height }, { placement: position, offset }));
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    // The dropdown's own size can change after it opens (an async list, an
    // image), which moves where its top-placed corner belongs.
    const ro = new ResizeObserver(measure);
    ro.observe(dropdownEl);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      ro.disconnect();
    };
  }, [isOpen, position, offset, dropdownEl]);

  useOnClickOutside(dropdownRef, () => { if (isOpen && closeOnClickOutside) setOpen(false); });

  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, closeOnEscape, setOpen]);

  const childArr = Children.toArray(children).filter(isValidElement) as ReactElement[];
  const target = childArr.find((c) => (c.type as { displayName?: string }).displayName === 'PopoverTarget');
  const dropdown = childArr.find((c) => (c.type as { displayName?: string }).displayName === 'PopoverDropdown');
  const targetChild = target ? (target.props as TargetProps).children : null;
  const dropdownChild = dropdown ? (dropdown.props as DropdownProps).children : null;

  // Inject ref + aria onto the anchor child.
  const enhancedTarget = isValidElement(targetChild)
    ? cloneElement(targetChild as ReactElement<{ ref?: React.Ref<HTMLElement>; 'aria-haspopup'?: string; 'aria-expanded'?: boolean; 'aria-controls'?: string; onClick?: (e: React.MouseEvent) => void }>, {
        ref: (node: HTMLElement | null) => { anchorRef.current = node; },
        'aria-haspopup': 'dialog',
        'aria-expanded': isOpen,
        'aria-controls': dropId,
        onClick: (e: React.MouseEvent) => {
          (targetChild as ReactElement<{ onClick?: (e: React.MouseEvent) => void }>).props.onClick?.(e);
          setOpen(!isOpen);
        }
      })
    : targetChild;

  return (
    <>
      {enhancedTarget}
      {isOpen ? (
        <Portal>
          <div
            ref={setDropdownNode}
            id={dropId}
            role="dialog"
            className={['rr-popover', `rr-popover--shadow-${shadow}`, className].filter(Boolean).join(' ')}
            style={{
              position: 'fixed',
              top: coords ? `${coords.y}px` : '-9999px',
              left: coords ? `${coords.x}px` : '-9999px',
              width: typeof width === 'number' ? `${width}px` : width,
              zIndex: 'var(--rr-z-popover)' as unknown as number,
              animationDuration: multiplier === 0 ? '0ms' : undefined
            }}
          >
            {dropdownChild}
            {withArrow && coords ? (
              <span
                className={`rr-popover__arrow rr-popover__arrow--${coords.arrow.side}`}
                style={{ left: coords.arrow.x, top: coords.arrow.y }}
                aria-hidden
              />
            ) : null}
          </div>
        </Portal>
      ) : null}
    </>
  );
}

Target.displayName = 'PopoverTarget';
Dropdown.displayName = 'PopoverDropdown';

interface PopoverComponent extends React.FC<PopoverProps> {
  Target: typeof Target;
  Dropdown: typeof Dropdown;
}

export const Popover = PopoverRoot as PopoverComponent;
Popover.Target = Target;
Popover.Dropdown = Dropdown;
